import { createIdentifier } from '@blocksuite/global/di';

import type { DataSource } from '../data-source/base.js';
import { Matcher_ } from '../logical/matcher.js';
import { groupByMatchers } from './define.js';
import type { GroupByConfig } from './types.js';

export const createGroupByMatcher = (list: GroupByConfig[]) => {
  return new Matcher_(list, v => v.matchType);
};

// Global registry for GroupByConfigs that cannot reach the dataSource DI
// (e.g. configs registered at editor-DI level but accessed from store-DI level).
// Call registerGlobalGroupByConfig() at module-load time from any package.
const globalGroupByRegistry: GroupByConfig[] = [];

export const registerGlobalGroupByConfig = (
  ...configs: GroupByConfig[]
): void => {
  configs.forEach(c => {
    if (!globalGroupByRegistry.some(r => r.name === c.name)) {
      globalGroupByRegistry.push(c);
    }
  });
};

export const findGroupByConfigByName = (
  dataSource: DataSource,
  name: string
): GroupByConfig | undefined => {
  const svc = getGroupByService(dataSource);
  const all: GroupByConfig[] = [
    ...svc.allExternalGroupByConfig(),
    ...groupByMatchers,
  ];
  return all.find(c => c.name === name);
};

export class GroupByService {
  constructor(private readonly dataSource: DataSource) {}

  allExternalGroupByConfig(): GroupByConfig[] {
    const diConfigs = Array.from(
      this.dataSource.provider.getAll(ExternalGroupByConfigProvider).values()
    );
    // Merge DI-registered configs with the global registry (dedup by name).
    const seen = new Set(diConfigs.map(c => c.name));
    const extra = globalGroupByRegistry.filter(c => !seen.has(c.name));
    return [...diConfigs, ...extra];
  }

  get matcher() {
    return createGroupByMatcher([
      ...this.allExternalGroupByConfig(),
      ...groupByMatchers,
    ]);
  }
}

export const GroupByProvider =
  createIdentifier<GroupByService>('group-by-service');

export const getGroupByService = (dataSource: DataSource) => {
  return dataSource.serviceGetOrCreate(
    GroupByProvider,
    () => new GroupByService(dataSource)
  );
};

export const ExternalGroupByConfigProvider = createIdentifier<GroupByConfig>(
  'external-group-by-config'
);
